/*
 * Copyright (c) Dito Technologies
 */

import React from "react";
import {graphql} from "babel-plugin-relay/macro";
import {createFragmentContainer} from "react-relay";
import PieceRenderer from "ui/feature/piece-play/common/piece-renderer/PieceRenderer";
import styles from "ui/feature/piece-play/simplified-note-by-note/SimplifiedNoteByNotePlayer.module.scss";
import MIDI, {MIDIEvent} from "state/midi";
import PlayTypeSelector from "ui/feature/piece-play/common/PlayTypeSelector";
import PlayType from "ui/feature/piece-play/common/PlayType";
import PieceRendererStepThru from "ui/feature/piece-play/common/piece-renderer/PieceRendererStepThru";
import SimplifiedNoteByNotePlayerFinished from "ui/feature/piece-play/simplified-note-by-note/SimplifiedNoteByNotePlayerFinished";
import {
    SimplifiedNoteByNotePlayer_Piece$data
} from "ui/feature/piece-play/simplified-note-by-note/__generated__/SimplifiedNoteByNotePlayer_Piece.graphql";
import {BulbIdToStateLookup, NoteDisplayState} from "ui/feature/piece-play/common/PlayTypes";

interface Props {
    piece: SimplifiedNoteByNotePlayer_Piece$data;
}

const SimplifiedNoteByNotePlayer: React.FC<Props> = ({piece}) => {
    const midiContext = React.useContext(MIDI)
    const [finished, setFinished] = React.useState<boolean>(false);
    const [playType, setPlayType] = React.useState<PlayType>("both_hands")
    const [bulbIdToStateLookup, setBulbIdToStateLookup] = React.useState<BulbIdToStateLookup>({});
    const [currStartTime, setCurrStartTime] = React.useState<number>(0)
    const [currentPressedNotes, setCurrentPressedNotes] = React.useState<Array<number>>([]);
    const currMidiValues: Array<{ midiNumber: number, bulbId: string }> = React.useMemo(() => {
        const file = piece.file;
        const output = new Array<{ midiNumber: number, bulbId: string }>();
        if (file) {
            for (const note of file.notes) {
                if (playType === "left_hand" && !note.left_handed) {
                    continue;
                } else if (playType === "right_hand" && note.left_handed) {
                    continue;
                }
                if (note.note_start === currStartTime) {
                    output.push({
                        midiNumber: note.midi_number,
                        bulbId: note.bulb_id
                    });
                }
            }
        }
        return output;
    }, [currStartTime, piece.file, playType])

    React.useEffect(() => {
        const currentNotes = new Set(currentPressedNotes);
        const targetNotes = new Set(currMidiValues.map((v) => v.midiNumber));
        if (currentNotes.size !== targetNotes.size && targetNotes.size > 0) {
            return;
        }
        // @ts-ignore
        for (const val of currentNotes) {
            if (!targetNotes.has(val)) {
                return;
            }
        }

        const file = piece.file;
        if (!file) {
            return;
        }

        // move to the next start time.
        let minimumStartTime = NaN;
        for (const note of file.notes) {
            if (note.note_start > currStartTime && (isNaN(minimumStartTime) || note.note_start < minimumStartTime)) {
                minimumStartTime = note.note_start;
            }
        }

        if (isNaN(minimumStartTime)) {
            // piece is over! need to handle this
            setFinished(true);
            return;
        } else {
            setCurrentPressedNotes([]); // clear the press!
            setCurrStartTime(minimumStartTime)
        }

        // let's mark the current notes as success.
        const newValues: { [bulbId: string]: NoteDisplayState } = Object.assign({}, ...currMidiValues.map((midiValue) => {
            const currValue: { [bulbId: string]: NoteDisplayState } = {
                [midiValue.bulbId]: "success"
            }
            return currValue;
        }));
        setBulbIdToStateLookup((oldValue) => ({
            ...oldValue,
            ...newValues
        }));
    }, [currentPressedNotes, currMidiValues, setBulbIdToStateLookup, bulbIdToStateLookup, currStartTime, piece.file]);
    const handleMIDIEvent = React.useCallback((e: MIDIEvent) => {
        if (e.event_type === "key_down") {
            setCurrentPressedNotes((prev) => {
                return [...prev, e.key];
            });
        } else if (e.event_type === "key_up") {
            setCurrentPressedNotes((prev) => {
                return prev.filter((k) => k !== e.key);
            });
        }
    }, [setCurrentPressedNotes])

    const setOnMIDIEvent = midiContext.setOnMIDIEvent;

    React.useEffect(() => {
        setOnMIDIEvent(() => handleMIDIEvent)
    }, [handleMIDIEvent, setOnMIDIEvent]);

    const restart = React.useCallback(() => {
        setCurrentPressedNotes([]);
        setCurrStartTime(0);
        setBulbIdToStateLookup({});
        setFinished(false);
    }, [setCurrStartTime, setFinished, setBulbIdToStateLookup]);

    return (
        <div className={styles.container}>
            {finished && <SimplifiedNoteByNotePlayerFinished restart={restart}/>}
            <PlayTypeSelector playType={playType} setPlayType={setPlayType}/>
            <PieceRenderer
                playType={playType}
                currentMidiNotes={currentPressedNotes}
                playPosition={currStartTime}
                piece={piece}
                bulbIdToStateLookup={{
                    ...bulbIdToStateLookup,
                    ...Object.assign({}, ...currMidiValues.map((midiValue) => {
                        const currValue: { [bulbId: string]: NoteDisplayState } = {
                            [midiValue.bulbId]: "current"
                        }
                        return currValue;
                    })),
                    ...Object.assign({}, ...currMidiValues.filter((midiValue) => {
                        return currentPressedNotes.includes(midiValue.midiNumber)
                    }).map((midiValue) => {
                        const currValue: { [bulbId: string]: NoteDisplayState } = {
                            [midiValue.bulbId]: "success"
                        }
                        return currValue;
                    }))
                }}/>
            <PieceRendererStepThru
                currTime={currStartTime}
                bulbIdToStateLookup={bulbIdToStateLookup}
                piece={piece}
                className={styles.stepThrough}/>
        </div>
    );
}

export default createFragmentContainer(SimplifiedNoteByNotePlayer, {
    piece: graphql`
    fragment SimplifiedNoteByNotePlayer_Piece on MusicPiece {
        id
        ...PieceRenderer_piece
        ...PieceRendererStepThru_piece
        file {
            notes {
                left_handed
                note_start
                bulb_id
                midi_number
            }
        }
    }
    `
});